import { z } from 'zod'
import type { ApiClient } from './client'
import { ackEnvelope, assertAck, flatCountEnvelope } from './envelope'

/**
 * Settings → Tenants (docs/settings-requirements.md §7).
 *
 * A TENANT IS A CLUSTER. Every endpoint here says `cluster`, the console says
 * tenant, and they are the same thing — the deployments in the rail's context
 * switcher and in `session.clusters` are these rows. Worth stating first
 * because the vocabulary split is total: no endpoint contains the word tenant
 * except `tenant-list`, and no screen shows the word cluster.
 *
 * This is where the organization ↔ tenant relationship is WRITTEN. The
 * Organizations section can only read it (§6.5): both association endpoints
 * are keyed by cluster, so associating and dissociating live here, and the
 * connection is only editable from this side.
 *
 * Three things worth knowing before the bindings:
 *
 *  1. THE ASSOCIATION KIND IS DERIVED, NOT CHOSEN. The first organization
 *     attached to a tenant is its PRIMARY; every later one is SECONDARY. The
 *     legacy screen computed that from a list length, which is a write derived
 *     from a possibly-unread list — see `nextAssociation`.
 *  2. ONLY A SECONDARY ASSOCIATION CAN BE REMOVED. The primary is structural;
 *     the legacy row renders no disconnect control for it.
 *  3. THE TWO SIDES OF ONE RELATIONSHIP USE DIFFERENT ENVELOPES. The org side
 *     nests (`data.tenantDetails`); this side is flat (`data`). Same
 *     relationship, two shapes, so this gets its own schema rather than
 *     borrowing `orgAssociationResponse`.
 */

/* ------------------------------------------------------------------ the row */

/**
 * A secondary organization as a tenant row carries it.
 *
 * The row denormalises its own associations: `primaryOrgName` is a bare string
 * and `secondaryOrg` is an array of objects. Two shapes for one relationship
 * ON ONE ROW, which is why neither is reused as the association row below.
 */
export const tenantSecondaryOrg = z
  .object({ orgName: z.string().default('') })
  .passthrough()

/**
 * One tenant — `POST /lc/mgmt/cluster/tenant-list`.
 *
 * `state` is the PROVISIONING lifecycle, not an enabled/disabled flag: a
 * tenant that never finished being set up is a real row that no amount of
 * configuration from this console can open. `isProvisioned` reads it.
 */
export const tenant = z
  .object({
    clusterId: z.string(),
    clusterName: z.string().default(''),
    /** 'configured' | 'connected' | 'validated' | an unfinished setup's own state. */
    state: z.string().default(''),
    licenseType: z.string().default(''),
    /** The tenant's primary organization, by NAME. There is no id on the row. */
    primaryOrgName: z.string().default(''),
    secondaryOrg: z.array(tenantSecondaryOrg).default([]),
  })
  .passthrough()

export type Tenant = z.output<typeof tenant>
export type TenantSecondaryOrg = z.output<typeof tenantSecondaryOrg>

export const tenantsResponse = flatCountEnvelope(tenant)

/**
 * The states in which a tenant is finished being set up.
 *
 * Read off the legacy list, which routes a row to the DETAIL page only for
 * these three and to the provisioning wizard otherwise. Any other value means
 * setup was started and not completed.
 */
export const PROVISIONED_STATES = ['configured', 'connected', 'validated'] as const

export function isProvisioned(t: Pick<Tenant, 'state'>): boolean {
  return (PROVISIONED_STATES as readonly string[]).includes(t.state)
}

/* ---------------------------------------------------------------- the detail */

/**
 * One tenant in full — `GET /lc/mgmt/cluster/{clusterId}`.
 *
 * `data` is an ARRAY OF ONE (`response.data.data[0]`), which is a THIRD detail
 * shape across Settings: a user's detail is an array of one, an organization's
 * is an object, and this is an array again. There is no rule to infer; each is
 * established from a capture, and assuming the wrong one renders an empty page
 * rather than an error.
 */
export const tenantCore = z
  .object({ coreIP: z.string().default('') })
  .passthrough()

export const tenantDetail = tenant.extend({
  coreList: z.array(tenantCore).default([]),
})

export type TenantDetail = z.output<typeof tenantDetail>

export const tenantDetailResponse = z.object({
  status: z.string().optional(),
  message: z.string().optional(),
  data: z.array(tenantDetail).default([]),
})

/* ----------------------------------------------------- the org association */

export const PRIMARY = 'primary'
export const SECONDARY = 'secondary'

/**
 * One organization associated with this tenant —
 * `POST /lc/mgmt/cluster/{clusterId}/org-association`.
 *
 * Carries `orgId`, which the tenant ROW's denormalised `primaryOrgName` and
 * `secondaryOrg[].orgName` do not. That is what makes dissociate possible from
 * here without the name-resolution dance the Users delete needs (§5.5) — the
 * id is on the row that offers the button.
 */
export const tenantOrg = z
  .object({
    orgId: z.string(),
    orgName: z.string().default(''),
    /** Prose. Matched by SUBSTRING, as the legacy screen did. */
    tenantAssociation: z.string().default(''),
    activeUsers: z.number().nullable().default(null),
  })
  .passthrough()

export type TenantOrg = z.output<typeof tenantOrg>

/**
 * FLAT — `{ status, count, data: [...] }`.
 *
 * The organization side of this same relationship nests its array under
 * `data.tenantDetails` (`orgAssociationResponse`). Reusing that schema here
 * would parse to an empty list, and on this screen an empty list is the answer
 * that makes an associate look necessary and a dissociate look unnecessary.
 */
export const tenantOrgsResponse = flatCountEnvelope(tenantOrg)

export function isPrimaryOrg(o: Pick<TenantOrg, 'tenantAssociation'>): boolean {
  return o.tenantAssociation.includes('Primary Organization')
}

/**
 * Whether this association may be removed.
 *
 * Only a SECONDARY can. The legacy row renders no disconnect control for the
 * primary at all — a tenant's primary organization is structural, and there is
 * no endpoint that replaces one.
 */
export function canDissociate(o: Pick<TenantOrg, 'tenantAssociation'>): boolean {
  return !isPrimaryOrg(o)
}

/**
 * Which kind the NEXT association will be.
 *
 * The first organization on a tenant is its primary; every later one is
 * secondary. The legacy screen derived this from `list.length === 0` on the
 * page it had rendered — a structural property decided by a client-side array,
 * which is wrong twice over: the list is PAGED, so `length` is not the count,
 * and while it is still loading `length` is 0, so an associate issued too
 * early would claim to be creating a primary for a tenant that already has one.
 *
 * So this takes the server's TOTAL and is undefined-safe: given no answer yet
 * it returns null, and the caller must not offer the write. `total` is the
 * only honest input.
 */
export function nextAssociation(total: number | null | undefined): string | null {
  if (typeof total !== 'number') return null
  return total === 0 ? PRIMARY : SECONDARY
}

/* -------------------------------------------------------------------- api */

export type TenantFilter = Record<string, unknown>

export interface TenantsQuery {
  page?: number
  size?: number
  query?: string
  filters?: TenantFilter[]
}

export interface TenantsPage {
  tenants: Tenant[]
  total: number
}

export interface TenantOrgsQuery {
  clusterId: string
  page?: number
  size?: number
  query?: string
}

export interface TenantOrgsPage {
  orgs: TenantOrg[]
  total: number
}

export interface AssociateOrgInput {
  clusterId: string
  orgId: string
  /** `primary` or `secondary`. Come by it through `nextAssociation`. */
  association: string
}

export interface DissociateOrgInput {
  clusterId: string
  orgId: string
}

export interface TenantsApi {
  /** The tenant directory. `page`/`size`, like the organization directory. */
  list: (q?: TenantsQuery) => Promise<TenantsPage>
  /** One tenant. `data` is an array of one; null when it holds nothing. */
  detail: (clusterId: string) => Promise<TenantDetail | null>
  /** The organizations on this tenant. FLAT envelope, unlike the org side. */
  orgs: (q: TenantOrgsQuery) => Promise<TenantOrgsPage>
  /** Attach an organization. The kind is derived, never picked. */
  associateOrg: (input: AssociateOrgInput) => Promise<void>
  /**
   * Detach a SECONDARY organization. `canDissociate` gates the offer.
   *
   * There is no `organizations()` here: the picker reads the SAME directory
   * Organizations owns (`organizationsApi.list`). One endpoint with two
   * bindings is what the platform-users split just removed; it does not come
   * back for the third reader.
   */
  dissociateOrg: (input: DissociateOrgInput) => Promise<void>
}

export function createTenantsApi(client: ApiClient): TenantsApi {
  return {
    list: async ({ page = 1, size = 50, query = '', filters = [] } = {}) => {
      const res = await client.request('POST', '/lc/mgmt/cluster/tenant-list', {
        // The running screen calls THIS, for both the grid and its filter
        // dropdown. `GET /lc/mgmt/cluster/list` is declared twice, against two
        // different hosts, and the call site is commented out — the same
        // two-generations situation as the plugin endpoints (§4.1), resolved
        // the same way: bind only what the running screen calls.
        body: { page, size, query, add_filter: filters },
        schema: tenantsResponse,
      })
      return { tenants: res.data, total: res.count }
    },

    detail: async (clusterId) => {
      const res = await client.request('GET', `/lc/mgmt/cluster/${encodeURIComponent(clusterId)}`, {
        schema: tenantDetailResponse,
      })
      // An array of one, or empty. Empty is a real answer — a tenant id that
      // no longer resolves — and is reported rather than rendered as blank.
      return res.data[0] ?? null
    },

    orgs: async ({ clusterId, page = 1, size = 50, query = '' }) => {
      const res = await client.request(
        'POST',
        `/lc/mgmt/cluster/${encodeURIComponent(clusterId)}/org-association`,
        { body: { page, size, query, add_filter: [] }, schema: tenantOrgsResponse },
      )
      return { orgs: res.data, total: res.count }
    },

    associateOrg: async ({ clusterId, orgId, association }) => {
      const res = await client.request(
        'POST',
        `/lc/mgmt/cluster/${encodeURIComponent(clusterId)}/org`,
        { body: { orgId, orgAssociation: association }, schema: ackEnvelope },
      )
      assertAck(res, 'Adding the organization to this tenant')
    },

    dissociateOrg: async ({ clusterId, orgId }) => {
      const res = await client.request(
        'POST',
        // The org id is in the PATH and there is no body at all.
        `/lc/mgmt/cluster/${encodeURIComponent(clusterId)}/org/dissociate/${encodeURIComponent(orgId)}`,
        { schema: ackEnvelope },
      )
      assertAck(res, 'Removing the organization from this tenant')
    },
  }
}
